import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { MaintenanceProvider } from './context/MaintenanceContext';
import { CartProvider } from './context/CartContext';
import { DeliveryAddressProvider } from './context/DeliveryAddressContext';
import { NotificationProvider } from './context/NotificationContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PageLayout from './components/PageLayout';
import NotificationContainer from './components/NotificationContainer';
import FloatingCartButton from './components/FloatingCartButton';
import FloatingMessageButton from './components/FloatingMessageButton';
import BackToTopButton from './components/BackToTopButton';
import ActiveOrderFloatingPopup from './components/ActiveOrderFloatingPopup';
import Home from './pages/Home';
import Landing from './pages/Landing';
import Restaurants from './pages/Restaurants';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CheckEmail from './pages/CheckEmail';
import VerifyEmail from './pages/VerifyEmail';
import Settings from './pages/Settings';
import Cart from './pages/Cart';
import Search from './pages/Search';
import Orders from './pages/Orders';
import Help from './pages/Help';
import Account from './pages/Account';
import Wallet from './pages/Wallet';
import Information from './pages/Information';
import Gdpr from './pages/Gdpr';
import GeneralTerms from './pages/GeneralTerms';
import GuidelinesReviews from './pages/GuidelinesReviews';
import Privacy from './pages/Privacy';
import RestaurantDetail from './pages/RestaurantDetail';
import StoreDetail from './pages/StoreDetail';
import Stores from './pages/Stores';
import PrivateRoute from './components/PrivateRoute';
import AdminLogin from './admin/pages/AdminLogin';
import AdminLayout from './admin/components/AdminLayout';
import AdminRoute from './admin/components/AdminRoute';
import MainAdminDashboard from './admin/pages/main-admin/Dashboard';
import MainAdminUsers from './admin/pages/main-admin/Users';
import MainAdminRestaurants from './admin/pages/main-admin/Restaurants';
import MainAdminStores from './admin/pages/main-admin/Stores';
import MainAdminVariationTemplates from './admin/pages/main-admin/VariationTemplates';
import MainAdminOrders from './admin/pages/main-admin/Orders';
import MainAdminMessages from './admin/pages/main-admin/Messages';
import MainAdminDiscountCodes from './admin/pages/main-admin/DiscountCodes';
import MainAdminContactSubmissions from './admin/pages/main-admin/ContactSubmissions';
import MainAdminBusinessIntelligence from './admin/pages/main-admin/BusinessIntelligence';
import MainAdminReviews from './admin/pages/main-admin/Reviews';
import MainAdminDelivery from './admin/pages/main-admin/Delivery';
import MainAdminInvoicing from './admin/pages/main-admin/Invoicing';
import MainAdminMediaGallery from './admin/pages/main-admin/MediaGallery';
import MainAdminLandingContent from './admin/pages/main-admin/LandingContent';
import MainAdminWebsiteSettings from './admin/pages/main-admin/WebsiteSettings';
import RestaurantOwnerDashboard from './admin/pages/restaurant-owner/Dashboard';
import RestaurantOwnerOrders from './admin/pages/restaurant-owner/Orders';
import RestaurantOwnerReviews from './admin/pages/restaurant-owner/Reviews';
import VenueBusinessIntelligence from './admin/pages/VenueBusinessIntelligence';
import StoreOwnerDashboard from './admin/pages/store-owner/Dashboard';
import StoreOwnerOrders from './admin/pages/store-owner/Orders';
import StoreOwnerReviews from './admin/pages/store-owner/Reviews';
import DriverDashboard from './admin/pages/driver/Dashboard';
import DriverOrders from './admin/pages/driver/Orders';
import DriverSettings from './admin/pages/driver/Settings';
import DriverBusinessIntelligence from './admin/pages/driver/BusinessIntelligence';
import DeliveryView from './admin/pages/driver/DeliveryView';
import MaintenanceGate from './components/MaintenanceGate';
import './App.css';
import './theme.css';

function ThemeSync() {
  const { user } = useAuth();
  const { setPreference } = useTheme();
  useEffect(() => {
    if (user?.settings?.themePreference) {
      setPreference(user.settings.themePreference);
    }
  }, [user?.settings?.themePreference, setPreference]);
  return null;
}

function App() {
  const { i18n } = useTranslation();

  return (
    <ThemeProvider>
      <AuthProvider>
        <CartProvider>
          <DeliveryAddressProvider>
            <NotificationProvider>
              <MaintenanceProvider>
                <Router>
                <ThemeSync />
                <MaintenanceGate>
                  <div className="App">
                    <NotificationContainer />
                    <FloatingCartButton />
                    <FloatingMessageButton />
                    <ActiveOrderFloatingPopup />
                    <BackToTopButton />
                    <Routes>
                {/* Admin routes without Navbar */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin/main/dashboard"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin']}>
                      <AdminLayout>
                        <MainAdminDashboard />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/main/users"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin']}>
                      <AdminLayout>
                        <MainAdminUsers />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/main/restaurants"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin']}>
                      <AdminLayout>
                        <MainAdminRestaurants />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/main/stores"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin']}>
                      <AdminLayout>
                        <MainAdminStores />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/main/variation-templates"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin']}>
                      <AdminLayout>
                        <MainAdminVariationTemplates />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/main/orders"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin']}>
                      <AdminLayout>
                        <MainAdminOrders />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/main/messages"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin']}>
                      <AdminLayout>
                        <MainAdminMessages />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/main/discount-codes"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin']}>
                      <AdminLayout>
                        <MainAdminDiscountCodes />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/main/contact-submissions"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin']}>
                      <AdminLayout>
                        <MainAdminContactSubmissions />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/main/business-intelligence"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin', 'invoicer']}>
                      <AdminLayout>
                        <MainAdminBusinessIntelligence />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/main/reviews"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin']}>
                      <AdminLayout>
                        <MainAdminReviews />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/main/delivery/*"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin']}>
                      <AdminLayout>
                        <MainAdminDelivery />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/main/invoicing"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin', 'invoicer']}>
                      <AdminLayout>
                        <MainAdminInvoicing />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/main/media-gallery"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin']}>
                      <AdminLayout>
                        <MainAdminMediaGallery />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/main/landing-content"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin']}>
                      <AdminLayout>
                        <MainAdminLandingContent />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/main/website-settings"
                  element={
                    <AdminRoute allowedRoles={['main_admin']}>
                      <AdminLayout>
                        <MainAdminWebsiteSettings />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/restaurant/dashboard"
                  element={
                    <AdminRoute allowedRoles={['restaurant_owner', 'restaurant_user']}>
                      <AdminLayout>
                        <RestaurantOwnerDashboard />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/restaurant/orders"
                  element={
                    <AdminRoute allowedRoles={['restaurant_owner', 'restaurant_user']}>
                      <AdminLayout>
                        <RestaurantOwnerOrders />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/restaurant/reviews"
                  element={
                    <AdminRoute allowedRoles={['restaurant_owner']}>
                      <AdminLayout>
                        <RestaurantOwnerReviews />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/restaurant/messages"
                  element={
                    <AdminRoute allowedRoles={['restaurant_owner', 'restaurant_user']}>
                      <AdminLayout>
                        <MainAdminMessages />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/restaurant/management"
                  element={
                    <AdminRoute allowedRoles={['restaurant_owner', 'restaurant_user']}>
                      <AdminLayout>
                        <MainAdminRestaurants />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/restaurant/media-gallery"
                  element={
                    <AdminRoute allowedRoles={['restaurant_owner', 'restaurant_user']}>
                      <AdminLayout>
                        <MainAdminMediaGallery />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/restaurant/business-intelligence"
                  element={
                    <AdminRoute allowedRoles={['restaurant_owner', 'restaurant_user']}>
                      <AdminLayout>
                        <VenueBusinessIntelligence />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/store/dashboard"
                  element={
                    <AdminRoute allowedRoles={['store_owner', 'store_user']}>
                      <AdminLayout>
                        <StoreOwnerDashboard />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/store/management"
                  element={
                    <AdminRoute allowedRoles={['store_owner', 'store_user']}>
                      <AdminLayout>
                        <MainAdminStores />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/store/orders"
                  element={
                    <AdminRoute allowedRoles={['store_owner', 'store_user']}>
                      <AdminLayout>
                        <StoreOwnerOrders />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/store/reviews"
                  element={
                    <AdminRoute allowedRoles={['store_owner', 'store_user']}>
                      <AdminLayout>
                        <StoreOwnerReviews />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/store/media-gallery"
                  element={
                    <AdminRoute allowedRoles={['store_owner', 'store_user']}>
                      <AdminLayout>
                        <MainAdminMediaGallery />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/store/business-intelligence"
                  element={
                    <AdminRoute allowedRoles={['store_owner', 'store_user']}>
                      <AdminLayout>
                        <VenueBusinessIntelligence />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/store/messages"
                  element={
                    <AdminRoute allowedRoles={['store_owner', 'store_user']}>
                      <AdminLayout>
                        <MainAdminMessages />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/driver/dashboard"
                  element={
                    <AdminRoute allowedRoles={['delivery_driver']}>
                      <AdminLayout>
                        <DriverDashboard />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/driver/orders"
                  element={
                    <AdminRoute allowedRoles={['delivery_driver']}>
                      <AdminLayout>
                        <DriverOrders />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/driver/settings"
                  element={
                    <AdminRoute allowedRoles={['delivery_driver']}>
                      <AdminLayout>
                        <DriverSettings />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/driver/messages"
                  element={
                    <AdminRoute allowedRoles={['delivery_driver']}>
                      <AdminLayout>
                        <MainAdminMessages />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/driver/business-intelligence"
                  element={
                    <AdminRoute allowedRoles={['delivery_driver']}>
                      <AdminLayout>
                        <DriverBusinessIntelligence />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/driver/delivery-view"
                  element={
                    <AdminRoute allowedRoles={['main_admin', 'admin', 'delivery_driver']}>
                      <AdminLayout>
                        <DeliveryView />
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
                
                {/* Regular app routes with Navbar */}
                <Route path="/login" element={<><Navbar /><Login /></>} />
                <Route path="/register" element={<><Navbar /><Register /></>} />
                <Route path="/forgot-password" element={<><Navbar /><ForgotPassword /></>} />
                <Route path="/reset-password" element={<><Navbar /><ResetPassword /></>} />
                <Route path="/check-email" element={<><Navbar /><CheckEmail /></>} />
                <Route path="/verify-email" element={<><Navbar /><VerifyEmail /></>} />
                <Route path="/" element={<PageLayout><Landing /></PageLayout>} />
                <Route path="/restaurants" element={<PageLayout><Restaurants /></PageLayout>} />
                <Route path="/stores" element={<PageLayout><Stores /></PageLayout>} />
                <Route path="/search" element={<PageLayout><Search /></PageLayout>} />
                <Route path="/restaurant/:id" element={<PageLayout><RestaurantDetail /></PageLayout>} />
                <Route path="/store/:id" element={<PageLayout><StoreDetail /></PageLayout>} />
                <Route
                  path="/settings"
                  element={
                    <PageLayout><PrivateRoute><Settings /></PrivateRoute></PageLayout>
                  }
                />
                <Route
                  path="/orders"
                  element={
                    <PageLayout><PrivateRoute><Orders /></PrivateRoute></PageLayout>
                  }
                />
                <Route path="/help" element={<PageLayout><Help /></PageLayout>} />
                <Route
                  path="/account"
                  element={
                    <PageLayout><PrivateRoute><Account /></PrivateRoute></PageLayout>
                  }
                />
                <Route
                  path="/wallet"
                  element={
                    <PageLayout><PrivateRoute><Wallet /></PrivateRoute></PageLayout>
                  }
                />
                <Route path="/information" element={<PageLayout><Information /></PageLayout>} />
                <Route path="/gdpr" element={<PageLayout><Gdpr /></PageLayout>} />
                <Route path="/general-terms" element={<PageLayout><GeneralTerms /></PageLayout>} />
                <Route path="/guidelines-reviews" element={<PageLayout><GuidelinesReviews /></PageLayout>} />
                <Route path="/privacy" element={<PageLayout><Privacy /></PageLayout>} />
                <Route path="/cart" element={<PageLayout><Cart /></PageLayout>} />
                    </Routes>
                  </div>
                </MaintenanceGate>
              </Router>
            </MaintenanceProvider>
          </NotificationProvider>
        </DeliveryAddressProvider>
      </CartProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
